import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, Pagination } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'
import { extractApiErrorMessage } from '../../utils/apiErrors'
import PaymentForm from './PaymentForm'
import PaymentDetail from './PaymentDetail'
import { formatCurrency } from '../../utils/formatters'
import {
  FORMA_PAGO_SAT_OPTIONS_PAGO,
  getFormaPagoSatLabel,
  paymentMethodToFormaPago,
} from '../../constants/formaPagoSat'

const PaymentsList = () => {
  const { user } = useAuth()
  const { clients } = useClientFilter()
  const {
    clientId: filterCliente,
    setClientId: setFilterCliente,
    selectDisabled,
  } = useSyncedLocalClientId()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [showCargaMasiva, setShowCargaMasiva] = useState(false)
  const [cargaFile, setCargaFile] = useState(null)
  const [cargaDryRun, setCargaDryRun] = useState(true)
  const [cargaResult, setCargaResult] = useState(null)
  const [cargaLoading, setCargaLoading] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterEstado, setFilterEstado] = useState('all')
  const [filterTipo, setFilterTipo] = useState('all')
  const [filterMetodo, setFilterMetodo] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  
  // Ordenamiento y paginación
  const [sortField, setSortField] = useState('payment_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Obtener lista de pagos
  const { data: paymentsData, isLoading, error } = useQuery(
    ['payments', searchTerm, filterEstado, filterTipo, filterCliente, filterMetodo, startDate, endDate],
    async () => {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterEstado !== 'all') params.append('estado', filterEstado)
      if (filterTipo !== 'all') params.append('tipo_pago', filterTipo)
      if (filterCliente) params.append('cliente_id', filterCliente)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      params.append('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField)
      
      const response = await api.get(`/financial/payments/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )
  
  // Filtrar localmente por forma de pago SAT
  const filteredPayments = filterMetodo !== 'all'
    ? (paymentsData || []).filter((p) => {
        const forma = (p.forma_pago && p.forma_pago !== '99')
          ? p.forma_pago
          : paymentMethodToFormaPago(p.payment_method)
        return forma === filterMetodo
      })
    : (paymentsData || [])
  
  // Ordenamiento local adicional (si es necesario)
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    
    if (sortField === 'payment_date') {
      aVal = new Date(aVal).getTime()
      bVal = new Date(bVal).getTime()
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal?.toLowerCase() || ''
    } else if (sortField === 'amount' || sortField === 'monto_aplicado' || sortField === 'monto_disponible') {
      aVal = parseFloat(aVal || 0)
      bVal = parseFloat(bVal || 0)
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })
  
  // Paginación
  const totalPages = Math.ceil(sortedPayments.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedPayments = sortedPayments.slice(startIndex, endIndex)
  
  // Manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }
  
  // Indicador de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted" style={{ fontSize: '0.75rem' }}></i>
    }
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-primary" style={{ fontSize: '0.75rem' }}></i>
      : <i className="bi bi-arrow-down text-primary" style={{ fontSize: '0.75rem' }}></i>
  }

  // Mutación para aplicar pago a facturas
  const applyPaymentMutation = useMutation(
    async ({ paymentId, aplicaciones }) => {
      return api.post(`/financial/payments/${paymentId}/aplicar/`, { aplicaciones })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('invoice')
        toast.success('Pago aplicado a facturas exitosamente')
      },
      onError: (error) => {
        console.error('Error al aplicar pago:', error)
        console.error('Error response data:', error.response?.data)
        
        const errorData = error.response?.data
        let message = 'Error al aplicar pago'
        
        if (errorData?.error) {
          message = typeof errorData.error === 'string' 
            ? errorData.error 
            : JSON.stringify(errorData.error)
        } else if (errorData?.message) {
          message = errorData.message
        } else if (errorData?.detail) {
          message = typeof errorData.detail === 'string'
            ? errorData.detail
            : Array.isArray(errorData.detail)
              ? errorData.detail.join(', ')
              : JSON.stringify(errorData.detail)
        }
        
        toast.error(message)
      }
    }
  )

  // Mutación para crear/actualizar pago
  const paymentMutation = useMutation(
    async (paymentData) => {
      // Separar aplicaciones del resto de los datos
      const { aplicaciones, ...paymentPayload } = paymentData
      
      let paymentResponse
      if (paymentData.id) {
        // Actualizar pago existente
        paymentResponse = await api.put(`/financial/payments/${paymentData.id}/`, paymentPayload)
      } else {
        // Crear nuevo pago
        paymentResponse = await api.post('/financial/payments/', paymentPayload)
      }
      
      const payment = paymentResponse.data
      
      // Si hay aplicaciones y el pago se creó/actualizó correctamente, aplicar pagos
      if (aplicaciones && aplicaciones.length > 0 && payment.id) {
        try {
          await applyPaymentMutation.mutateAsync({
            paymentId: payment.id,
            aplicaciones: aplicaciones.filter(app => app.monto > 0)
          })
        } catch (error) {
          // Si la aplicación falla, no revertir el pago, solo mostrar error
          console.error('Error al aplicar pagos:', error)
          throw error
        }
      }
      
      return paymentResponse
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('pending-invoices')
        queryClient.invalidateQueries('invoice')
        setShowForm(false)
        setSelectedPayment(null)
        toast.success(selectedPayment ? 'Pago actualizado exitosamente' : 'Pago creado exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar pago'
        toast.error(message)
      }
    }
  )

  // Mutación para eliminar pago
  const deleteMutation = useMutation(
    async (paymentId) => {
      return api.delete(`/financial/payments/${paymentId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('invoice')
        toast.success('Pago eliminado exitosamente')
      },
      onError: (error) => {
        toast.error(extractApiErrorMessage(error, 'No se pudo eliminar el pago'))
      }
    }
  )

  const handleCreate = () => {
    setSelectedPayment(null)
    setShowForm(true)
  }

  const handleDescargarPlantillaPagos = async () => {
    try {
      const response = await api.get('/financial/payments/plantilla-carga-masiva/', {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'plantilla_carga_masiva_pagos.csv'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'No se pudo descargar la plantilla'))
    }
  }

  const handleCargaMasivaPagos = async () => {
    if (!cargaFile) {
      toast.error('Seleccione un archivo CSV o XLSX')
      return
    }
    setCargaLoading(true)
    setCargaResult(null)
    try {
      const fd = new FormData()
      fd.append('archivo', cargaFile)
      fd.append('dry_run', cargaDryRun ? '1' : '0')
      const name = (cargaFile.name || '').toLowerCase()
      fd.append('formato', name.endsWith('.xlsx') ? 'xlsx' : 'csv')
      const { data } = await api.post('/financial/payments/carga-masiva/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setCargaResult(data)
      if (data.ok) {
        toast.success(
          cargaDryRun
            ? `Simulación OK: ${data.creados} fila(s)`
            : `Creados ${data.creados} pago(s)`
        )
        if (!cargaDryRun) {
          queryClient.invalidateQueries('payments')
          queryClient.invalidateQueries('invoices')
        }
      } else {
        toast.warning(`Procesado con ${data.errores} error(es); creados: ${data.creados}`)
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Error en carga masiva'))
    } finally {
      setCargaLoading(false)
    }
  }

  const handleEdit = (payment) => {
    setSelectedPayment(payment)
    setShowForm(true)
  }

  const handleView = (payment) => {
    setSelectedPayment(payment)
    setShowDetail(true)
  }

  const handleDelete = (payment) => {
    if (payment.can_delete === false) {
      toast.warning(
        payment.delete_blocked_reason
          || 'Este pago no se puede eliminar porque está aplicado a facturas. Cancele el pago primero.'
      )
      return
    }
    if (window.confirm('¿Está seguro de eliminar este pago? Esta acción no se puede deshacer.')) {
      deleteMutation.mutate(payment.id)
    }
  }

  const handleSubmit = (formData) => {
    paymentMutation.mutate(formData)
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { bg: 'warning', text: 'Pendiente' },
      'aplicado': { bg: 'success', text: 'Aplicado' },
      'cancelado': { bg: 'danger', text: 'Cancelado' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getTipoBadge = (tipo) => {
    const badges = {
      'ingreso': { bg: 'success', text: 'Ingreso' },
      'egreso': { bg: 'danger', text: 'Egreso' }
    }
    const badge = badges[tipo] || { bg: 'secondary', text: tipo }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getPaymentMethodLabel = (payment) => {
    if (payment?.forma_pago_display) return payment.forma_pago_display
    const forma = (payment?.forma_pago && payment.forma_pago !== '99')
      ? payment.forma_pago
      : paymentMethodToFormaPago(payment?.payment_method)
    return getFormaPagoSatLabel(forma) || payment?.payment_method || 'N/A'
  }

  // Estadísticas
  const stats = {
    total: filteredPayments.length,
    pendiente: filteredPayments.filter(p => p.estado === 'pendiente').length,
    aplicado: filteredPayments.filter(p => p.estado === 'aplicado').length,
    cancelado: filteredPayments.filter(p => p.estado === 'cancelado').length,
    ingreso: filteredPayments.filter(p => p.tipo_pago === 'ingreso').length,
    egreso: filteredPayments.filter(p => p.tipo_pago === 'egreso').length,
    totalAmount: filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    aplicadoAmount: filteredPayments.reduce((sum, p) => sum + parseFloat(p.monto_aplicado || 0), 0),
    disponibleAmount: filteredPayments.reduce((sum, p) => sum + parseFloat(p.monto_disponible || 0), 0)
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3>Pagos</h3>
          <p className="text-muted mb-0">
            <small>Total: {stats.total} | Monto Total: {formatCurrency(stats.totalAmount)}</small>
          </p>
        </div>
        <Button variant="outline-secondary" className="me-2" onClick={() => {
          setShowCargaMasiva(true)
          setCargaResult(null)
          setCargaFile(null)
        }}>
          Carga masiva
        </Button>
        <Button variant="primary" onClick={handleCreate}>
          + Nuevo Pago
        </Button>
      </div>

      {/* Estadísticas rápidas */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center border-primary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-primary">{stats.total}</h5>
              <small className="text-muted">Total</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-warning">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-warning">{stats.pendiente}</h5>
              <small className="text-muted">Pendientes</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-success">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{stats.aplicado}</h5>
              <small className="text-muted">Aplicados</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-danger">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-danger">{stats.cancelado}</h5>
              <small className="text-muted">Cancelados</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-info">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-info">
                ${stats.aplicadoAmount.toLocaleString('es-MX', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </h5>
              <small className="text-muted">Monto Aplicado</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-secondary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-secondary">
                ${stats.disponibleAmount.toLocaleString('es-MX', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </h5>
              <small className="text-muted">Disponible</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros avanzados */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label><small>Búsqueda</small></Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar por referencia, cliente..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label><small>Cliente</small></Form.Label>
              <Form.Select
                value={filterCliente}
                onChange={(e) => {
                  setFilterCliente(e.target.value)
                  setCurrentPage(1)
                }}
                disabled={selectDisabled}
                title={selectDisabled ? 'Controlado por el filtro global del encabezado' : undefined}
              >
                <option value="">Todos los clientes</option>
                {(clients || []).map((client) => (
                  <option key={client.id} value={String(client.id)}>
                    {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno}`}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label><small>Estado</small></Form.Label>
              <Form.Select
                value={filterEstado}
                onChange={(e) => {
                  setFilterEstado(e.target.value)
                  setCurrentPage(1)
                }}
              >
                <option value="all">Todos los estados</option>
                <option value="pendiente">Pendiente</option>
                <option value="aplicado">Aplicado</option>
                <option value="cancelado">Cancelado</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label><small>Tipo</small></Form.Label>
              <Form.Select
                value={filterTipo}
                onChange={(e) => {
                  setFilterTipo(e.target.value)
                  setCurrentPage(1)
                }}
              >
                <option value="all">Todos los tipos</option>
                <option value="ingreso">Ingreso</option>
                <option value="egreso">Egreso</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label><small>Forma de pago</small></Form.Label>
              <Form.Select
                value={filterMetodo}
                onChange={(e) => {
                  setFilterMetodo(e.target.value)
                  setCurrentPage(1)
                }}
              >
                <option value="all">Todos</option>
                {FORMA_PAGO_SAT_OPTIONS_PAGO.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={1} className="text-end">
              <Form.Label><small>&nbsp;</small></Form.Label>
              <Badge bg="secondary" className="d-block">
                {stats.total}
              </Badge>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Label><small>Fecha desde</small></Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label><small>Fecha hasta</small></Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSearchTerm('')
                  setFilterCliente('')
                  setFilterEstado('all')
                  setFilterTipo('all')
                  setFilterMetodo('all')
                  setStartDate('')
                  setEndDate('')
                  setCurrentPage(1)
                }}
              >
                Limpiar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de pagos */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">
          Error al cargar pagos. Por favor, intenta nuevamente.
        </Alert>
      ) : sortedPayments.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted">No se encontraron pagos</p>
            <Button variant="primary" onClick={handleCreate}>
              Crear primer pago
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('payment_date')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Fecha
                      {getSortIcon('payment_date')}
                    </span>
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('cliente_name')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Cliente
                      {getSortIcon('cliente_name')}
                    </span>
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('tipo_pago')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Tipo
                      {getSortIcon('tipo_pago')}
                    </span>
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('amount')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Monto
                      {getSortIcon('amount')}
                    </span>
                  </th>
                  <th>Forma de pago</th>
                  <th>Referencia</th>
                  <th>Factura(s)</th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('monto_aplicado')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Aplicado
                      {getSortIcon('monto_aplicado')}
                    </span>
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('monto_disponible')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Disponible
                      {getSortIcon('monto_disponible')}
                    </span>
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('estado')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Estado
                      {getSortIcon('estado')}
                    </span>
                  </th>
                  <th style={{ minWidth: '200px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPayments.map((payment) => {
                  const montoAplicado = parseFloat(payment.monto_aplicado || 0)
                  const montoDisponible = parseFloat(payment.monto_disponible || payment.amount || 0)
                  const montoTotal = parseFloat(payment.amount || 0)
                  const porcentajeAplicado = montoTotal > 0 ? (montoAplicado / montoTotal) * 100 : 0
                  const porcentajeDisponible = montoTotal > 0 ? (montoDisponible / montoTotal) * 100 : 0
                  
                  return (
                    <tr key={payment.id}>
                      <td>{formatDateOnlyEsMX(payment.payment_date, {}, '—')}</td>
                      <td>
                        {payment.cliente_name || 
                         (payment.cliente?.razon_social || payment.cliente?.business_name) || 
                         'N/A'}
                      </td>
                      <td>{getTipoBadge(payment.tipo_pago)}</td>
                      <td>
                        <strong>
                          {formatCurrency(montoTotal)}
                        </strong>
                      </td>
                      <td>
                        <Badge bg="info">{getPaymentMethodLabel(payment)}</Badge>
                      </td>
                      <td>{payment.reference || '-'}</td>
                      <td style={{ maxWidth: 220 }}>
                        {(() => {
                          const docs = payment.documentos_aplicados
                            || (payment.aplicaciones || [])
                              .filter((a) => (a.estado || '') === 'aplicado')
                              .map((a) => ({
                                tipo: a.tipo_documento || (a.bill_uuid ? 'bill' : 'invoice'),
                                label: a.tipo_documento === 'bill' || a.bill_uuid
                                  ? (a.bill_uuid || '').slice(0, 8) || 'Bill'
                                  : (a.factura_number || `#${a.factura || a.id}`),
                                monto: a.monto,
                              }))
                          if (!docs.length) {
                            return <span className="text-muted">—</span>
                          }
                          return (
                            <div className="d-flex flex-column gap-1">
                              {docs.map((d, idx) => (
                                <small key={`${d.tipo}-${d.label}-${idx}`} className="text-nowrap">
                                  <Badge bg={d.tipo === 'bill' ? 'secondary' : 'primary'} className="me-1 fw-normal">
                                    {d.tipo === 'bill' ? 'Recibida' : 'Factura'}
                                  </Badge>
                                  {d.label}
                                </small>
                              ))}
                            </div>
                          )
                        })()}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className={montoAplicado > 0 ? 'text-success fw-bold' : 'text-muted'}>
                            {formatCurrency(montoAplicado)}
                          </span>
                          {montoAplicado > 0 && (
                            <Badge bg="success" style={{ fontSize: '0.7rem' }}>
                              {porcentajeAplicado.toFixed(0)}%
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className={montoDisponible > 0 ? 'text-info fw-bold' : 'text-muted'}>
                            {formatCurrency(montoDisponible)}
                          </span>
                          {montoDisponible > 0 && (
                            <Badge bg="info" style={{ fontSize: '0.7rem' }}>
                              {porcentajeDisponible.toFixed(0)}%
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td>{getEstadoBadge(payment.estado)}</td>
                      <td style={{ whiteSpace: 'nowrap', minWidth: '200px', textAlign: 'center' }}>
                        <div className="d-flex gap-2 justify-content-center flex-wrap">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleView(payment)}
                            title="Ver detalles"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-eye me-1"></i>
                            <span>Ver</span>
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleEdit(payment)}
                            title="Editar"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            <span>Editar</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(payment)}
                            disabled={deleteMutation.isLoading || payment.can_delete === false}
                            title={payment.delete_blocked_reason || 'Eliminar'}
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            
            {/* Paginación */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Mostrar:</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                  <span className="text-muted">
                    {startIndex + 1}-{Math.min(endIndex, sortedPayments.length)} de {sortedPayments.length}
                  </span>
                </div>
                
                <Pagination className="mb-0">
                  <Pagination.First 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  />
                  <Pagination.Prev 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  />
                  
                  {/* Números de página (máximo 5 visibles) */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    )
                  })}
                  
                  <Pagination.Next 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  />
                  <Pagination.Last 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      <Modal show={showForm} onHide={() => { setShowForm(false); setSelectedPayment(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedPayment ? 'Editar Pago' : 'Nuevo Pago'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PaymentForm
            payment={selectedPayment}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setSelectedPayment(null) }}
            isLoading={paymentMutation.isLoading}
          />
        </Modal.Body>
      </Modal>

      {/* Modal de detalle */}
      <Modal show={showDetail} onHide={() => { setShowDetail(false); setSelectedPayment(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle del Pago</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPayment && (
            <PaymentDetail
              payment={selectedPayment}
              onEdit={() => {
                setShowDetail(false)
                handleEdit(selectedPayment)
              }}
              onRefresh={() => {
                queryClient.invalidateQueries('payments')
                queryClient.invalidateQueries('invoice')
                setShowDetail(false)
                setSelectedPayment(null)
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showCargaMasiva} onHide={() => !cargaLoading && setShowCargaMasiva(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Carga masiva de pagos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Crea pago + aplicación por fila. Columnas: rfc_cliente, payment_date, amount, forma_pago,
            moneda, tipo_cambio, reference, uuid_factura (o serie+folio), monto_aplicado.
          </p>
          <Button variant="link" className="px-0 mb-3" onClick={handleDescargarPlantillaPagos}>
            Descargar plantilla CSV
          </Button>
          <Form.Group className="mb-3">
            <Form.Label>Archivo CSV / XLSX</Form.Label>
            <Form.Control
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => setCargaFile(e.target.files?.[0] || null)}
            />
          </Form.Group>
          <Form.Check
            type="switch"
            id="carga-dry-run"
            className="mb-3"
            label="Simular (dry-run) sin guardar"
            checked={cargaDryRun}
            onChange={(e) => setCargaDryRun(e.target.checked)}
          />
          {cargaResult && (
            <Alert variant={cargaResult.ok ? 'success' : 'warning'}>
              Creados: {cargaResult.creados} · Errores: {cargaResult.errores}
              {cargaResult.detalle_errores?.length > 0 && (
                <ul className="mb-0 mt-2 small">
                  {cargaResult.detalle_errores.slice(0, 10).map((e) => (
                    <li key={e.fila}>Fila {e.fila}: {e.error}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={cargaLoading} onClick={() => setShowCargaMasiva(false)}>
            Cerrar
          </Button>
          <Button variant="primary" disabled={cargaLoading || !cargaFile} onClick={handleCargaMasivaPagos}>
            {cargaLoading ? 'Procesando…' : (cargaDryRun ? 'Simular' : 'Cargar')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PaymentsList

